import { Modal, Table, Tag, Select, Skeleton } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useState } from "react";
import { useGetCampaignStats } from "../hooks/useGetCampaignStats";
import { useGetCampaignSignups } from "../hooks/useGetCampaignSignups";
import { TCampaignSignup, TMarketingCampaign } from "../types";

type Props = {
  open: boolean;
  handleClose: () => void;
  campaign: TMarketingCampaign;
};

export const CampaignStatsModal = ({ open, handleClose, campaign }: Props) => {
  const [page, setPage] = useState(1);
  const [signupType, setSignupType] = useState<string | undefined>();
  const { data: stats, isLoading: statsLoading } = useGetCampaignStats(
    open ? campaign.id : undefined
  );
  const { data: signups, isLoading: signupsLoading } = useGetCampaignSignups(
    open ? campaign.id : undefined,
    page,
    signupType
  );

  const columns: ColumnsType<TCampaignSignup> = [
    {
      title: "Name",
      key: "name",
      render: (_, r) => r.user?.name ?? "—",
    },
    {
      title: "Email",
      key: "email",
      render: (_, r) => r.user?.email ?? "—",
    },
    {
      title: "Type",
      dataIndex: "signup_type",
      key: "signup_type",
      render: (t: string) => (
        <Tag color={t === "talent" ? "blue" : "purple"}>{t}</Tag>
      ),
    },
    {
      title: "Method",
      dataIndex: "attribution_method",
      key: "attribution_method",
    },
    {
      title: "Signed up",
      dataIndex: "created_at",
      key: "created_at",
      render: (v: string) => (v ? new Date(v).toLocaleString() : "—"),
    },
  ];

  const copyLink = async (url?: string | null) => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // ignore
    }
  };

  return (
    <Modal
      title={`Campaign: ${campaign.name}`}
      open={open}
      onCancel={handleClose}
      footer={null}
      width="56rem"
      style={{ top: 20 }}
      destroyOnClose
    >
      <div className="mb-4 space-y-2 text-sm">
        <div className="flex flex-wrap gap-2 items-center">
          <Tag color={campaign.is_currently_active ? "green" : "default"}>
            {campaign.is_currently_active ? "Active" : "Inactive"}
          </Tag>
          <code className="bg-gray-100 px-2 py-1 rounded">{campaign.code}</code>
        </div>
        <div className="flex flex-col gap-1">
          <div>
            <span className="text-gray-500">Short tracking link: </span>
            <button
              type="button"
              className="text-primary underline break-all text-left"
              onClick={() => copyLink(campaign.tracking_url)}
            >
              {campaign.tracking_url || "—"} (click to copy)
            </button>
          </div>
          <div>
            <span className="text-gray-500">Direct signup link: </span>
            <button
              type="button"
              className="text-primary underline break-all text-left"
              onClick={() => copyLink(campaign.signup_url)}
            >
              {campaign.signup_url || "—"} (click to copy)
            </button>
          </div>
        </div>
      </div>

      {statsLoading ? (
        <Skeleton active />
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
          {[
            { label: "Clicks", value: stats?.total_clicks ?? 0 },
            { label: "Unique visitors", value: stats?.unique_visitors ?? 0 },
            { label: "Total signups", value: stats?.total_signups ?? 0 },
            { label: "Talent signups", value: stats?.talent_signups ?? 0 },
            {
              label: "Recruiter signups",
              value: stats?.recruiter_signups ?? 0,
            },
            {
              label: "Conversion %",
              value: stats?.conversion_rate ?? 0,
            },
          ].map((m) => (
            <div
              key={m.label}
              className="rounded-lg border border-gray-100 bg-gray-50 p-3"
            >
              <div className="text-xs text-gray-500">{m.label}</div>
              <div className="text-xl font-semibold text-primary">{m.value}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex justify-between items-center mb-3">
        <h4 className="font-semibold text-primary m-0">Attributed signups</h4>
        <Select
          allowClear
          placeholder="Filter type"
          className="min-w-[9rem]"
          value={signupType}
          onChange={(v) => {
            setSignupType(v);
            setPage(1);
          }}
          options={[
            { value: "talent", label: "Talent" },
            { value: "recruiter", label: "Recruiter" },
          ]}
        />
      </div>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={signups?.data ?? []}
        loading={signupsLoading}
        size="small"
        pagination={{
          current: signups?.current_page ?? page,
          total: signups?.total ?? 0,
          pageSize: signups?.per_page ?? 15,
          onChange: setPage,
        }}
      />
    </Modal>
  );
};
